import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import isDateValid from "date-fns/isValid";
import parseDate from "date-fns/parse";
import { Form, Formik, FormikProps } from "formik";
import React, { useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Yup from "yup";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";
import TestRequestAproeApi from "../../../api/services/TestRequestAproeApi";
import CustomButton from "../../../components/Button/CustomButton";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import { NavigationContext } from "../../../contexts/NavigationContext";
import { TestRequestContext } from "../../../contexts/TestRequestContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import scrollToError from "../../../functions/scrollToError.function";
import { dropdownRequiredErrorMessage } from "../../../helper/YupValidationErrors";
import useEnv from "../../../hooks/env";
import ValidationError from "../../../models/ValidationError";
import { tesaDarkRedError } from "../../../styles/colors";
import ContainerFadeIn from "../ContainerFadeIn";
import CustomFooterGraphic from "../CustomFooterGraphic";
import EstimatedFinishingDateInput from "../DescriptionTab/inputs/EstimatedFinishingDateInput";
import AproeActionsMenu from "./AproeActionsMenu";
import AproeDropdown from "./AproeDropdown";
import mapToUpdateRequest from "./AproeProject.request-mapper";
import mapFromResponse from "./AproeProject.response-mapper";
import * as Labels from "./fieldLabels";
import * as Fields from "./fieldNames";
import { AproeAction, AproeProjectForm } from "./models";
import { COMPLEXITY_OPTIONS, SOLUTION_TYPE_OPTIONS, SUPPORT_TYPE_OPTIONS, TAPE_USED_OPTIONS } from "./selectOptions";

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    "& > * + *": {
      marginLeft: theme.spacing(3),
    },
  },
  gridContainer: {
    marginTop: "-8px",

    "& .MuiGrid-item.pt-4": {
      paddingTop: "4px",
    },
  },
}));

const DEFAULT_FORM_VALUES: AproeProjectForm = {
  [Fields.AWAITING_FEEDBACK]: false,
  [Fields.COMPLEXITY]: "",
  [Fields.FINISHING_DATE]: null,
  [Fields.SOLUTION_TYPE]: "",
  [Fields.SUPPORT_TYPE]: "",
  [Fields.TAPE_USED]: "",
};

const validationSchema = Yup.object().shape({
  [Fields.COMPLEXITY]: Yup.string().nullable().required(dropdownRequiredErrorMessage),
  [Fields.TAPE_USED]: Yup.string().nullable().required(dropdownRequiredErrorMessage),
  [Fields.SOLUTION_TYPE]: Yup.string().nullable().required(dropdownRequiredErrorMessage),
  [Fields.SUPPORT_TYPE]: Yup.string().nullable().required(dropdownRequiredErrorMessage),
});

function dateOrNull(dateString: string) {
  const parsed = parseDate(dateString, "yyyy-MM-dd", new Date());
  return isDateValid(parsed) ? parsed : null;
}

function AproeProjectTab() {
  const { api } = useEnv();
  const classes = useStyles();
  const { updateDirtyFlag, updatePreventNav } = useContext(NavigationContext);
  const permissions = useContext(TestRequestPermissionContext);
  const { editProjectInfo, updatePermissionState } = permissions || {};
  const history = useHistory();
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const { testRequestObj, updateTestRequestObj } = useContext(TestRequestContext);
  const testRequestAproeApi = new TestRequestAproeApi(api);
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const initialValues = mapFromResponse(testRequestObj?.projectInfo);

  const refresh = (requestData: TestRequestResponse, redirect?: boolean) => {
    updateTestRequestObj(requestData);
    updateDirtyFlag(false);
    updatePreventNav(null);

    const requestsPage = document.getElementById("requestsPage");
    requestsPage?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
    updatePermissionState()
      .then(() => {
        if (redirect) {
          history.push(`/requests/${requestData.requestId}`);
        }
      })
      .catch(handleHttpError);
  };

  const validateForm = (formikProps: FormikProps<AproeProjectForm>, action: AproeAction) => {
    const validatedActions: AproeAction[] = ["PROTOTYPING", "IMPLEMENTATION"];
    const mustValidate = validatedActions.includes(action);
    const { validateForm, handleSubmit } = formikProps;
    if (mustValidate) {
      return validateForm().then((errors) => {
        const errorKeys = Object.keys(errors) as Array<keyof AproeProjectForm>;
        if (errorKeys.length > 0) {
          handleSubmit();
          scrollToError();
          const errorMessage = errorKeys.length === 1 ? errors[errorKeys[0]] : undefined;
          throw new ValidationError(errors, typeof errorMessage !== "string" ? undefined : errorMessage);
        }
      });
    }
    return Promise.resolve();
  };

  const handleSave = (formikProps: FormikProps<AproeProjectForm>, externalDirty?: boolean) => {
    const { resetForm, values } = formikProps;
    const dirty = externalDirty || formikProps.dirty;
    if (dirty) {
      return testRequestAproeApi
        .updateProjectInformation(testRequestId, mapToUpdateRequest(values))
        .then(({ data }) => {
          showSnackbar("Successfully saved.", "success");
          resetForm({ values });
          refresh(data);
        })
        .catch(handleHttpError);
    }
    return Promise.resolve();
  };
  const submitFormOnNav = (formikProps: FormikProps<AproeProjectForm>): Promise<void> => {
    return handleSave(formikProps, true);
  };
  const getPreventNav = (formikProps: FormikProps<AproeProjectForm>) => (): Promise<void> => {
    return showConfirmationDialog({
      headline: "You have unsaved changes",
      text: "If you continue, your current changes to the project information will be discarded.",
      confirmText: "Save",
      additionalAction: {
        id: "DISCARD",
        text: "Discard",
        textColor: tesaDarkRedError,
      },
    }).then((id: string) => {
      if (id === "DEFAULT") {
        updateDirtyFlag(true);
        submitFormOnNav(formikProps);
      }
      updateDirtyFlag(false);
    });
  };
  const handleDirtyChange = (formikProps: FormikProps<AproeProjectForm>, resetFlag?: boolean) => {
    if (resetFlag) {
      updateDirtyFlag(false);
      updatePreventNav(null);
    } else {
      updateDirtyFlag(true);
      updatePreventNav(getPreventNav(formikProps));
    }
  };
  const getCurriedDirtyChange = (formikProps: FormikProps<AproeProjectForm>) => () => handleDirtyChange(formikProps);
  const getOnActionPreSave = (formikProps: FormikProps<AproeProjectForm>) => (action: AproeAction) =>
    validateForm(formikProps, action).then(() => handleSave(formikProps));
  const handleStatusChangeSuccess = (data: TestRequestResponse) => refresh(data);

  return (
    <ContainerFadeIn>
      <Formik onSubmit={() => {}} initialValues={initialValues} validationSchema={validationSchema}>
        {(formikProps) => {
          const { dirty, isSubmitting, setFieldValue, values } = formikProps;
          const { handleChange } = formikProps;
          const handleLocalChange = (e: React.ChangeEvent<unknown>) => {
            handleChange(e);
          };

          useEffect(() => {
            if (dirty) {
              handleDirtyChange(formikProps);
            }
          }, [dirty, values]);

          return (
            <Form>
              <Grid container justify="space-between" direction="row" spacing={4} className={classes.gridContainer}>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <AproeDropdown
                    disabled={!editProjectInfo}
                    fieldIdentifier={Fields.COMPLEXITY}
                    labelText={Labels.COMPLEXITY}
                    onChange={handleLocalChange}
                    options={COMPLEXITY_OPTIONS}
                    FormikProps={formikProps}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={6}>
                  <EstimatedFinishingDateInput
                    estimatedFinishingDate={testRequestObj?.projectInfo?.estimatedFinishingDate}
                    disabled={!editProjectInfo}
                    disablePast={!!editProjectInfo}
                    handleDirtyChange={getCurriedDirtyChange(formikProps)}
                    showConfirmationDialog={showConfirmationDialog}
                    {...formikProps}
                    values={{
                      estimatedFinishingDate: values.estimatedFinishingDate || "",
                      deadline: testRequestObj?.description?.deadline || "",
                    }}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <AproeDropdown
                    disabled={!editProjectInfo}
                    fieldIdentifier={Fields.TAPE_USED}
                    labelText={Labels.TAPE_USED}
                    onChange={handleLocalChange}
                    options={TAPE_USED_OPTIONS}
                    FormikProps={formikProps}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <AproeDropdown
                    disabled={!editProjectInfo}
                    fieldIdentifier={Fields.SUPPORT_TYPE}
                    labelText={Labels.SUPPORT_TYPE}
                    onChange={handleLocalChange}
                    options={SUPPORT_TYPE_OPTIONS}
                    FormikProps={formikProps}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <AproeDropdown
                    disabled={!editProjectInfo}
                    fieldIdentifier={Fields.SOLUTION_TYPE}
                    labelText={Labels.SOLUTION_TYPE}
                    onChange={handleLocalChange}
                    options={SOLUTION_TYPE_OPTIONS}
                    FormikProps={formikProps}
                  />
                </Grid>
                <Grid item xs={12} className="pt-4">
                  <FormControlLabel
                    disabled={!editProjectInfo}
                    control={
                      <Switch
                        checked={values[Fields.AWAITING_FEEDBACK]}
                        color="secondary"
                        onChange={() => {
                          handleDirtyChange(formikProps);
                          setFieldValue(Fields.AWAITING_FEEDBACK, !values[Fields.AWAITING_FEEDBACK]);
                        }}
                        name={Fields.AWAITING_FEEDBACK}
                      />
                    }
                    label={Labels.AWAITING_FEEDBACK}
                  />
                </Grid>
                <Grid container item xs={12} direction="row" justify="flex-end">
                  <div className={classes.buttonContainer}>
                    <AproeActionsMenu
                      disabled={isSubmitting}
                      preSave={getOnActionPreSave(formikProps)}
                      onSuccess={handleStatusChangeSuccess}
                    />
                    {editProjectInfo && (
                      <CustomButton
                        disabled={isSubmitting}
                        onClick={() => handleSave(formikProps)}
                        primary
                        text="Save"
                        type="button"
                      />
                    )}
                  </div>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <CustomFooterGraphic />
    </ContainerFadeIn>
  );
}

export default AproeProjectTab;
